import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrefs, useLabels, useSitemap } from 'ws-scripts/modules/common';
import Background from '../components/Background';
import ContentWrapper from '../components/ContentWrapper';
import { setHover } from '../actions/hover';
import { setClassNames, setDialogClasses } from '../utilities/classes';
import { setWidgetConfiguration } from '../utilities/configuration';
import { triggerTrackingEvent } from '../utilities/analytics';
import { getHtmlWithDLData } from '../utilities/helpers/text';
import '../global-sass/main.scss';
import { isStringAlias, setUrl } from '../utilities/helpers/links';
import { getCtaType } from '../utilities/helpers/wiapi';

const setWrapperClassNames = (
	enableClickable,
	isHovered,
	imageLocation,
	...classes
) => {
	const classNames = ['cta', 'd-flex', 'position-relative', ...classes];

	if (
		imageLocation === 'background-right' ||
		imageLocation === 'background-left'
	) {
		classNames.push('flex-row');

		if (imageLocation === 'background-right') {
			classNames.push('flex-row-reverse');
		}
	} else {
		classNames.push('flex-column');
	}

	if (enableClickable) {
		classNames.push('pointer', 'text-no-decoration');

		if (isHovered) {
			classNames.push('hover');
		}
	}

	return setClassNames.apply(this, classNames);
};

const setStyles = (widgetHeight = null) => {
	const styles = {};

	if (widgetHeight) {
		styles.height = widgetHeight;
	}

	return styles;
};

const handleClick = (
	event,
	url,
	enableOpenNewWindow,
	elementCTA,
	windowId,
	isClickableModal,
	wiapiOnClick
) => {
	event.preventDefault();

	triggerTrackingEvent(
		'ws-content-cta Call to action',
		elementCTA,
		`Navigated to ${url}`,
		windowId
	);

	if (wiapiOnClick) {
		/** WIAPI is hijacking the default click behaviour of the widget */
		wiapiOnClick();
	} else if (!isClickableModal) {
		if (enableOpenNewWindow) {
			window.open(url);
		} else {
			window.location.href = url;
		}
	}
};

const Widget = () => {
	const prefs = usePrefs();
	const labels = useLabels();
	const sitemap = useSitemap();
	const [wiapiOverrides, setWiapiOverrides] = useState();

	const dispatch = useDispatch();
	const {
		hover: { isHovered },
		widgetInfo: { deviceType, windowId }
	} = useSelector(state => state);

	const initWiapiHandler = () => {
		const currentCtaUrl = setUrl(sitemap, (prefs.url || '').trim());
		const currentCtaAlias = isStringAlias(prefs.url) ? prefs.url : '';
		const ctaType = getCtaType(currentCtaUrl, currentCtaAlias);

		const handleWiapiModificationUpdate = ({
			detail: { location } = {}
		}) => {
			if (ctaType === location) {
				setWiapiOverrides({
					location,
					...window.DDC.PrivateAPI.getPageLevelModifications(location)
				});
			}
		};

		window.addEventListener(
			'wiapiPageModificationUpdate',
			handleWiapiModificationUpdate
		);

		// On widget load check if there is any page level modification already
		// available for the widget type. If, so call the wiapi modification handler
		if (
			window.DDC.PrivateAPI &&
			window.DDC.PrivateAPI.getPageLevelModifications(ctaType)
		) {
			handleWiapiModificationUpdate({
				detail: {
					location: ctaType
				}
			});
		}
	};

	// On widget mount.
	// Wire up the wiapi event listener
	useEffect(initWiapiHandler, []);

	const { displayStyle } = prefs;
	const widgetConfiguration = setWidgetConfiguration(
		deviceType,
		isHovered,
		displayStyle,
		prefs,
		sitemap,
		windowId,
		wiapiOverrides
	);
	const {
		widgetHeight,
		dialogWindowWidth,
		enableDialogWindow,
		enableOpenNewWindow,
		enableClickable,
		url,
		wiapiOnClick,
		showButton,
		imageLocation,
		headingLabel,
		contentLabel,
		buttonLabel,
		accessibleDescription,
		dialogWindowTitleLabel,
		widgetBackgroundColor,
		widgetTransitionProperty,
		widgetTransitionTimingFunction,
		widgetTransitionDuration,
		backgroundWrapperWidth,
		wiapiDataAttr
	} = widgetConfiguration;

	const isClickable = enableClickable && url !== '';
	const isAnchorElement = isClickable && !showButton;
	const isClickableDivElement = isClickable && !isAnchorElement;
	const isClickableModal =
		enableDialogWindow && (isClickableDivElement || isAnchorElement);
	const WidgetElement = isAnchorElement ? 'a' : 'div';
	const target = enableOpenNewWindow && !enableDialogWindow ? '_blank' : '';

	const headingText = headingLabel && labels.getHTML(headingLabel);
	const dialogWindowTitleText = labels.get(dialogWindowTitleLabel);
	const ariaLabel = labels.get(accessibleDescription);

	return (
		<WidgetElement
			className={setWrapperClassNames(
				enableClickable,
				isHovered,
				imageLocation,
				widgetBackgroundColor,
				widgetTransitionProperty,
				widgetTransitionTimingFunction,
				widgetTransitionDuration,
				setDialogClasses(isClickableModal, deviceType)
			)}
			style={setStyles(widgetHeight)}
			href={isAnchorElement ? url : null}
			aria-label={ariaLabel || null}
			aria-labelledby={
				!ariaLabel ? `${windowId}-heading ${windowId}-button` : null
			}
			role={isClickableDivElement ? 'button' : null}
			data-href={isClickableModal ? url : null}
			data-modal={isClickableModal ? 'true' : null}
			data-title={
				isClickableModal && dialogWindowTitleText
					? dialogWindowTitleText
					: null
			}
			data-width={
				isClickableModal && dialogWindowWidth ? dialogWindowWidth : null
			}
			target={isAnchorElement && target ? target : null}
			onClick={
				isClickable
					? event =>
							handleClick(
								event,
								url,
								enableOpenNewWindow,
								headingText,
								windowId,
								isClickableModal,
								wiapiOnClick
							)
					: null
			}
			onMouseEnter={() => enableClickable && dispatch(setHover(true))}
			onMouseLeave={() => enableClickable && dispatch(setHover(false))}
			{...(isAnchorElement && wiapiDataAttr)}
		>
			{imageLocation !== 'foreground' && (
				<div
					className={backgroundWrapperWidth}
					style={{ overflow: 'hidden' }}
				>
					<Background {...widgetConfiguration} />
				</div>
			)}
			<ContentWrapper
				{...{
					widgetConfiguration,
					windowId,
					deviceType,
					isClickableDivElement,
					imageLocation,
					headingText,
					contentText:
						contentLabel &&
						getHtmlWithDLData(labels.get(contentLabel)),
					buttonText: buttonLabel && labels.getHTML(buttonLabel),
					dialogWindowTitleText,
					target
				}}
			/>
		</WidgetElement>
	);
};

export default Widget;
